import { useEffect, useRef } from 'react';

/**
 * Restores keyboard focus to whatever element opened a modal/panel once it closes,
 * instead of leaving focus to fall back to <body> when the modal's own DOM — including
 * whatever was focused inside it, e.g. its close button — unmounts.
 *
 * THE BUG THIS FIXES (present, before this hook, in every modal/side-panel/inline
 * overlay in this app): each one's `if (!isOpen) return null;` (or equivalent) removes
 * its entire subtree — including whatever element currently has focus — from the DOM
 * the instant it closes. Per browser default, when the focused element is removed,
 * focus silently falls back to <body>; nothing else claims it. The user doesn't notice
 * anything happened until their very next Tab press, which then starts iterating from
 * the very first focusable element in the ENTIRE document (typically something in the
 * header/logo) instead of continuing from the button/link they just used to open (and
 * now close) the modal. That's what "focus gets tossed back up to the top of the page"
 * looks like in practice. It's disorienting for sighted keyboard users, and for
 * screen-reader users it means losing their place in the page entirely — a real WCAG
 * 2.4.3 (Focus Order) failure.
 *
 * Usage — one line, alongside whatever focus-trap / Escape-to-close / initial-focus
 * effects a modal already has. This hook is intentionally independent of all of that:
 * it only ever touches focus AFTER the modal has closed, so it's safe to add without
 * touching any existing logic.
 *
 *   useModalFocusReturn(isOpen);
 *
 * Works for any boolean "is this thing open" flag, so it equally covers non-portaled,
 * in-page "modal-overlay" sections (e.g. the "Check specialties" popups rendered
 * inline in providerDetailsType2/3/4) and side panels (distance, location, all-filters),
 * not just the dedicated <Modal>-style components.
 *
 * NOTE: A few places in this codebase (providerSummary.jsx's virtual-care-modal
 * trigger, browseBy.jsx, emailModal.jsx) already solved this same problem by hand with
 * their own local ref + focus() call. Those are left as-is rather than migrated to this
 * hook, to avoid touching already-correct behavior — but new modals should prefer this
 * shared hook over reinventing the same fix again.
 */
export function useModalFocusReturn(isOpen) {
  const triggerElementRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      // Not open — nothing to capture, nothing to restore.
      return undefined;
    }

    // Just opened — remember whatever had focus a moment ago. In practice this is
    // always the button/link that was just activated to open this modal. IMPORTANT:
    // call this hook before any other effect in the component that moves focus INTO
    // the modal (e.g. "focus the close button on open") — effects run in declaration
    // order, so if this hook is declared first, it captures the real outside trigger;
    // if declared after, it would wrongly capture the modal's own close button.
    triggerElementRef.current = document.activeElement;

    // This cleanup covers BOTH ways a modal "closes" in this codebase, so callers
    // don't need to know or care which pattern their component follows:
    //   1. `isOpen` flips back to `false` while the component stays mounted (e.g.
    //      distancePanel/locationPanel/providerFilters' sidebar, and the inline
    //      "Check specialties" overlays in providerDetailsType2/3/4, all of which
    //      render conditionally but never unmount the component itself) — React
    //      runs this cleanup right before re-running the effect with the new value.
    //   2. The parent stops rendering the modal entirely, e.g.
    //      `{showModal && <SomeModal isOpen={showModal} ... />}` — the moment
    //      `showModal` goes false, React unmounts `<SomeModal>` outright rather than
    //      re-rendering it with `isOpen={false}`, so this unmount cleanup is the
    //      *only* signal we ever get that it closed.
    return () => {
      const trigger = triggerElementRef.current;
      triggerElementRef.current = null;
      if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
        // Deferred one tick: mirrors the one place in this codebase that already
        // solved this manually (providerSummary.jsx's virtual-care-modal
        // workaround) — calling .focus() synchronously in the same tick the modal's
        // DOM unmounts/hides has proven unreliable in some browsers; a macrotask
        // tick later is consistently reliable.
        setTimeout(() => trigger.focus({ preventScroll: true }), 0);
      }
    };
  }, [isOpen]);
}

export default useModalFocusReturn;
